import type { AppConfigI } from 'appConfigs/example';

type MethodicVariants = NonNullable<AppConfigI['methodic']>['variants'];

/** Same account-group shape lw-chat-js accepts in its portal session check. */
const ACCOUNT_GROUP_PATTERN = /^account-(\d{1,8})$/;

/**
 * Reads the account number from an OIDC profile `group` claim.
 * @param profileGroup the `group` claim, a single entry or a list
 */
const getAccountNumber = (profileGroup: unknown): number | undefined => {
	const groups = Array.isArray(profileGroup) ? profileGroup : [profileGroup];
	const match = groups
		.map((group) =>
			typeof group === 'string'
				? ACCOUNT_GROUP_PATTERN.exec(group.trim())
				: null,
		)
		.find(Boolean);

	return match ? Number(match[1]) : undefined;
};

/**
 * Picks the Methodic agent variant for an account, in proportion to the
 * variants' weights. The same account always gets the same variant.
 * @param profileGroup the OIDC profile `group` claim holding `account-<n>`
 * @param variants the configured variants, in assignment order
 * @returns the variant key, or `undefined` when there is nothing to assign
 */
export default function selectAgentVariant(
	profileGroup: unknown,
	variants: MethodicVariants,
): string | undefined {
	const accountNumber = getAccountNumber(profileGroup);
	const totalWeight = (variants ?? []).reduce(
		(total, { weight }) => total + weight,
		0,
	);
	if (accountNumber === undefined || !variants || totalWeight <= 0) {
		return undefined;
	}

	let bucket = accountNumber % totalWeight;
	return variants.find(({ weight }) => {
		bucket -= weight;
		return bucket < 0;
	})?.key;
}
